// Push notifications — web (VAPID via SW pushManager) and native (FCM via
// @capacitor/push-notifications). The web path ports notifications.js; the native
// path registers an FCM token and POSTs it in the {platform:'android', token}
// shape the backend's send_push_notification branches on (see Phase 2).
//
// Native branch is scaffolding — full on-device verification (incl. listener
// lifecycle + the RuStore no-Play-services case) happens with a real device.

import { useCallback } from 'react'
import { PushNotifications } from '@capacitor/push-notifications'
import { messageFromError } from '../api/client'
import { pushApi } from '../api/push'
import { profileApi } from '../api/profile'
import { toast } from '../store/toastStore'
import { useAuthStore } from '../store/authStore'
import { isNative } from '../lib/platform'
import {
  clearPushDeviceDisabled,
  disablePushDevice,
  isPushDeviceDisabled,
  nativeBoundUserKey,
  nativeTokenKey,
} from '../lib/pushDevice'

const webSupported =
  typeof window !== 'undefined' && 'Notification' in window && 'serviceWorker' in navigator

const nativeTokenTimeoutMs = 15_000

let nativeRegistrationInFlight: {
  userId: string
  promise: Promise<void>
} | null = null

let nativeRefreshInFlight: {
  userId: string
  promise: Promise<void>
} | null = null

let webRefreshInFlight: {
  userId: string
  promise: Promise<void>
} | null = null

async function performNativeRegistration(userId: string) {
  if (useAuthStore.getState().userId !== userId) return
  if (isPushDeviceDisabled(userId)) return

  // A token previously registered for another account must not remain valid:
  // otherwise notifications for that account could reach the current session.
  if (localStorage.getItem(nativeBoundUserKey) !== userId) {
    await PushNotifications.unregister()
  }

  if (useAuthStore.getState().userId !== userId) return

  let resolveToken!: (token: string) => void
  let rejectToken!: (error: unknown) => void
  const tokenPromise = new Promise<string>((resolve, reject) => {
    resolveToken = resolve
    rejectToken = reject
  })
  let tokenTimeout: ReturnType<typeof setTimeout> | null = null
  let registrationHandle: Awaited<ReturnType<typeof PushNotifications.addListener>> | null = null
  let errorHandle: Awaited<ReturnType<typeof PushNotifications.addListener>> | null = null

  try {
    registrationHandle = await PushNotifications.addListener(
      'registration',
      ({ value }) => resolveToken(value),
    )
    if (useAuthStore.getState().userId !== userId) return
    errorHandle = await PushNotifications.addListener(
      'registrationError',
      (error) => rejectToken(error),
    )
    if (useAuthStore.getState().userId !== userId) return
    const timeoutPromise = new Promise<never>((_, reject) => {
      tokenTimeout = setTimeout(
        () => reject(new Error('FCM registration timed out')),
        nativeTokenTimeoutMs,
      )
    })
    const token = await Promise.race([
      PushNotifications.register().then(() => tokenPromise),
      timeoutPromise,
    ])

    if (
      useAuthStore.getState().userId !== userId
      || isPushDeviceDisabled(userId)
    ) {
      await PushNotifications.unregister()
      return
    }

    await pushApi.subscribe({ platform: 'android', token })
    if (
      useAuthStore.getState().userId !== userId
      || isPushDeviceDisabled(userId)
    ) {
      try {
        await pushApi.unsubscribeDevice({ platform: 'android', token })
      } catch {
        /* unregister below still invalidates the late token locally */
      }
      await PushNotifications.unregister()
      return
    }
    await profileApi.saveSettings({ notifications_enabled: true })
    if (
      useAuthStore.getState().userId !== userId
      || isPushDeviceDisabled(userId)
    ) {
      try {
        await pushApi.unsubscribeDevice({ platform: 'android', token })
      } catch {
        /* unregister below still invalidates the late token locally */
      }
      await PushNotifications.unregister()
      return
    }
    localStorage.setItem(nativeBoundUserKey, userId)
    localStorage.setItem(nativeTokenKey, token)
  } finally {
    if (tokenTimeout) clearTimeout(tokenTimeout)
    await registrationHandle?.remove()
    await errorHandle?.remove()
  }
}

async function registerNativeForUser(userId: string) {
  while (nativeRegistrationInFlight) {
    const active = nativeRegistrationInFlight
    await active.promise
    if (active.userId === userId) return
  }

  if (useAuthStore.getState().userId !== userId) return
  if (isPushDeviceDisabled(userId)) return

  const promise = performNativeRegistration(userId)
  nativeRegistrationInFlight = { userId, promise }
  try {
    await promise
  } finally {
    if (nativeRegistrationInFlight?.promise === promise) {
      nativeRegistrationInFlight = null
    }
  }
}

export function usePushNotifications() {
  // ---- WEB: VAPID via the service worker pushManager (notifications.js port) ----
  const subscribeWeb = useCallback(async () => {
    const registration = await navigator.serviceWorker.ready
    const { public_key } = await pushApi.vapidKey()
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: public_key,
    })
    await pushApi.subscribe(subscription.toJSON())
    await profileApi.saveSettings({ notifications_enabled: true })
  }, [])

  const enableWeb = useCallback(async () => {
    if (!webSupported) {
      toast('Браузер не поддерживает уведомления', 'error')
      return false
    }
    if (Notification.permission === 'denied') {
      toast('Уведомления заблокированы в настройках браузера', 'error')
      return false
    }
    if (Notification.permission !== 'granted') {
      const result = await Notification.requestPermission()
      if (result !== 'granted') return false
    }
    try {
      const userId = useAuthStore.getState().userId
      if (userId) clearPushDeviceDisabled(userId)
      await subscribeWeb()
      return true
    } catch (e) {
      toast(`Ошибка подписки: ${messageFromError(e)}`, 'error')
      return false
    }
  }, [subscribeWeb])

  const disableWeb = useCallback(async () => {
    const userId = useAuthStore.getState().userId
    if (!userId) return false
    const disabled = await disablePushDevice(userId, pushApi.unsubscribeDevice)
    if (!disabled) toast('Не удалось отключить уведомления', 'error')
    return disabled
  }, [])

  const refreshWeb = useCallback(async () => {
    if (!webSupported || Notification.permission !== 'granted') return
    const userId = useAuthStore.getState().userId
    if (!userId || isPushDeviceDisabled(userId)) return
    if (webRefreshInFlight?.userId === userId) {
      await webRefreshInFlight.promise
      return
    }
    const promise = (async () => {
      try {
        const registration = await navigator.serviceWorker.ready
        const subscription = await registration.pushManager.getSubscription()
        if (subscription) {
          const subscriptionJson = subscription.toJSON()
          await pushApi.subscribe(subscriptionJson)
          if (
            useAuthStore.getState().userId !== userId
            || isPushDeviceDisabled(userId)
          ) {
            try {
              await pushApi.unsubscribeDevice(subscriptionJson)
            } catch {
              /* local unsubscribe below may still invalidate the endpoint */
            }
            try {
              await subscription.unsubscribe()
            } catch {
              /* backend removal above may already have disabled delivery */
            }
          }
        }
      } catch {
        /* ignore */
      }
    })()
    webRefreshInFlight = { userId, promise }
    try {
      await promise
    } finally {
      if (webRefreshInFlight?.promise === promise) webRefreshInFlight = null
    }
  }, [])

  // ---- NATIVE: FCM via @capacitor/push-notifications ----
  const enableNative = useCallback(async () => {
    try {
      const userId = useAuthStore.getState().userId
      if (!userId) return false
      clearPushDeviceDisabled(userId)
      const perm = await PushNotifications.requestPermissions()
      if (perm.receive !== 'granted') {
        toast('Уведомления заблокированы', 'error')
        return false
      }
      if (useAuthStore.getState().userId !== userId) return false
      await registerNativeForUser(userId)
      return true
    } catch (e) {
      toast(`Ошибка подписки: ${messageFromError(e)}`, 'error')
      return false
    }
  }, [])

  const disableNative = useCallback(async () => {
    const userId = useAuthStore.getState().userId
    if (!userId) return false
    const disabled = await disablePushDevice(userId, pushApi.unsubscribeDevice)
    if (!disabled) {
      toast('Не удалось отключить уведомления', 'error')
      return false
    }
    return true
  }, [])

  const refreshNative = useCallback(async () => {
    const userId = useAuthStore.getState().userId
    if (!userId) return
    if (isPushDeviceDisabled(userId)) return
    if (nativeRefreshInFlight?.userId === userId) {
      await nativeRefreshInFlight.promise
      return
    }

    const promise = (async () => {
      const permission = await PushNotifications.checkPermissions()
      if (useAuthStore.getState().userId !== userId) return
      if (isPushDeviceDisabled(userId)) return
      if (permission.receive === 'granted') {
        await registerNativeForUser(userId)
        return
      }
      if (permission.receive === 'prompt' || permission.receive === 'prompt-with-rationale') {
        const requested = await PushNotifications.requestPermissions()
        if (useAuthStore.getState().userId !== userId) return
        if (requested.receive === 'granted') await registerNativeForUser(userId)
      }
    })()
    nativeRefreshInFlight = { userId, promise }
    try {
      await promise
    } finally {
      if (nativeRefreshInFlight?.promise === promise) nativeRefreshInFlight = null
    }
  }, [])

  const isNativeEnabled = useCallback(async () => {
    const userId = useAuthStore.getState().userId
    if (!userId || isPushDeviceDisabled(userId)) return false
    const permission = await PushNotifications.checkPermissions()
    return permission.receive === 'granted'
      && localStorage.getItem(nativeBoundUserKey) === userId
      && Boolean(localStorage.getItem(nativeTokenKey))
  }, [])

  const isWebEnabled = useCallback(async () => {
    if (!webSupported || Notification.permission !== 'granted') return false
    const userId = useAuthStore.getState().userId
    if (userId && isPushDeviceDisabled(userId)) return false
    const registration = await navigator.serviceWorker.ready
    return Boolean(await registration.pushManager.getSubscription())
  }, [])

  return {
    supported: isNative || webSupported,
    enable: isNative ? enableNative : enableWeb,
    disable: isNative ? disableNative : disableWeb,
    refresh: isNative ? refreshNative : refreshWeb,
    isEnabled: isNative ? isNativeEnabled : isWebEnabled,
    permission: isNative ? 'default' : webSupported ? Notification.permission : 'unsupported',
  }
}
